import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from 'react-helmet-async';

import Header from "../../components/Header";
import Footer from "../../components/Footer";
import Icon from "../../components/AppIcon";
import Image from "../../components/AppImage";
import Button from "../../components/ui/Button";

import RelatedPosts from "./components/RelatedPosts";
import ResourcesSection from "./components/ResourcesSection";
import NewsletterSection from "./components/NewsletterSection";
import {
  BlogPost,
  BlogCategory,
  Author,
  RelatedPost,
} from "./types";
import { getBlogPostBySlug, getRelatedBlogPosts } from "./posts";

// same category setup used on blog list
const categories: BlogCategory[] = [
  {
    id: "tutorials",
    name: "Tutorials",
    slug: "tutorials",
    description: "Step-by-step automation guides",
    icon: "BookOpen",
    color: "#2563EB",
  },
  {
    id: "case-studies",
    name: "Case Studies",
    slug: "case-studies",
    description: "Real-world automation success stories",
    icon: "FileText",
    color: "#10B981",
  },
  {
    id: "strategy",
    name: "Strategy",
    slug: "strategy",
    description: "Automation strategy & best practices",
    icon: "Target",
    color: "#F59E0B",
  },
  {
    id: "tool-guides",
    name: "Tool Guides",
    slug: "tool-guides",
    description: "Platform-specific tutorials",
    icon: "Wrench",
    color: "#EF4444",
  },
];

const defaultAuthor: Author = {
  id: "prince",
  name: "Prince Kumar",
  role: "Founder, Make Mind Automations",
  avatar: "https://images.unsplash.com/photo-1544723795-3fb6469f5b39",
  avatarAlt: "Portrait of automation consultant",
  bio: "Automation strategist helping businesses save time with n8n, Make.com and AI workflows.",
  socialLinks: {
    twitter: "https://twitter.com",
    linkedin: "https://linkedin.com",
  },
};

const BlogPostPage = () => {
  const params = useParams();
  const slug = params.slug as string;

  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<RelatedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // local analytics state
  const [localViews, setLocalViews] = useState<number | null>(null);
  const [localLikes, setLocalLikes] = useState<number | null>(null);
  const [liking, setLiking] = useState(false);

  useEffect(() => {
    if (!slug) return;

    setLoading(true);
    setError(null);

    // Static lookup: no Supabase call. See src/pages/blog/posts.ts.
    const found = getBlogPostBySlug(slug);

    if (!found) {
      setError("Post not found");
      setLoading(false);
      return;
    }

    setPost(found);
    setLocalViews(found.views ?? 0);
    setLocalLikes(found.likes ?? 0);
    setRelatedPosts(getRelatedBlogPosts(slug, 3));
    setLoading(false);
  }, [slug]);

  // Views are session-local for static posts: no Supabase update.
  useEffect(() => {
    if (!post?.id) return;

    const key = `mma_viewed_post_${post.id}`;
    if (typeof window !== "undefined" && sessionStorage.getItem(key)) {
      return;
    }

    if (typeof window !== "undefined") {
      sessionStorage.setItem(key, "true");
    }
  }, [post?.id]);


  const handleLike = () => {
    if (!post?.id || liking) return;

    // prevent multiple likes from same browser for this post
    const likeKey = `mma_liked_post_${post.id}`;
    if (typeof window !== "undefined" && localStorage.getItem(likeKey)) {
      console.log("Already liked this post from this browser.");
      return;
    }

    setLiking(true);

    // Session-local like for static posts: no Supabase update.
    const currentLikes = localLikes ?? post.likes ?? 0;
    setLocalLikes(currentLikes + 1);

    if (typeof window !== "undefined") {
      localStorage.setItem(likeKey, "true");
    }

    setLiking(false);
  };


  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <>
      
        <Header />
        <main className="min-h-screen flex items-center justify-center">
          <p className="text-text-secondary">Loading article...</p>
        </main>
        <Footer />
      </>
    );
  }

  if (error || !post) {
    return (
      <>
        <Header />
        <main className="min-h-screen flex flex-col items-center justify-center px-4 text-center space-y-4">
          <h1 className="text-2xl font-bold text-text-primary">
            {error === "Post not found" ? "Article not found" : "Something went wrong"}
          </h1>
          <p className="text-text-secondary max-w-md">
            {error
              ? error
              : "We couldn't load this blog post. Please try again later."}
          </p>
          <Button asChild>
            <Link to="/blog">
              <Icon name="ArrowLeft" className="w-4 h-4 mr-2" />
              Back to Blog
            </Link>
          </Button>
        </main>
        <Footer />
      </>
    );
  }

  const publishedLabel = post.publishedAt.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const viewsToShow = localViews ?? post.views ?? 0;
  const likesToShow = localLikes ?? post.likes ?? 0;

  return (
    <>
      <Helmet>
  <title>{post.title} | Make Mind Automations</title>
  <meta name="description" content={post.excerpt} />

  <link
    rel="canonical"
    href={`https://www.makemindautomations.tech/blog/${post.slug}`}
  />

  <meta property="og:type" content="article" />
  <meta property="og:title" content={`${post.title} | Make Mind Automations`} />
  <meta property="og:description" content={post.excerpt} />
  <meta
    property="og:url"
    content={`https://www.makemindautomations.tech/blog/${post.slug}`}
  />
</Helmet>


      <div className="min-h-screen bg-background">
        <Header />

        <main className="max-w-6xl mx-auto px-4 py-10 space-y-10">
          {/* Breadcrumb */}
          <nav className="text-xs text-text-secondary mb-2 flex items-center space-x-2">
            <Link to="/blog" className="hover:text-primary">
              Blog
            </Link>
            <span>/</span>
            <span className="text-text-primary line-clamp-1">{post.title}</span>
          </nav>

          {/* Hero area */}
          <article className="space-y-8">
            <header className="space-y-4">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
                <Icon name={post.category.icon} className="w-3.5 h-3.5" />
                <span>{post.category.name}</span>
                {post.isNew && (
                  <>
                    <span>•</span>
                    <span>New</span>
                  </>
                )}
              </div>

              <h1 className="text-3xl md:text-4xl font-bold text-text-primary">
                {post.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-xs text-text-secondary">
                <div className="flex items-center space-x-2">
                  <Image
                    src={post.author.avatar}
                    alt={post.author.avatarAlt}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-medium text-text-primary">
                      {post.author.name}
                    </div>
                    <div>{post.author.role}</div>
                  </div>
                </div>

                <span>•</span>

                <div className="flex items-center space-x-1">
                  <Icon name="Calendar" className="w-3.5 h-3.5" />
                  <span>{publishedLabel}</span>
                </div>

                <span>•</span>

                <div className="flex items-center space-x-1">
                  <Icon name="Clock" className="w-3.5 h-3.5" />
                  <span>{post.readTime} min read</span>
                </div>

                <span>•</span>

                <div className="flex items-center space-x-1">
                  <span>{viewsToShow} views</span>
                </div>

                <span>•</span>

                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleLike}
                  disabled={liking}
                >
                  👍 Like ({likesToShow})
                </Button>
              </div>
            </header>

            {/* Featured image */}
            <div className="rounded-2xl overflow-hidden border border-border">
              <Image
                src={post.featuredImage}
                alt={post.featuredImageAlt}
                className="w-full h-72 md:h-96 object-cover"
              />
            </div>

            {/* Main content */}
            <div className="grid lg:grid-cols-[3fr,1fr] gap-10">
              <div className="prose prose-neutral max-w-none prose-headings:text-text-primary prose-a:text-primary prose-li:marker:text-primary">
                {/* Render rich HTML from ReactQuill */}
                <div dangerouslySetInnerHTML={{ __html: post.content }} />
              </div>

              {/* Sidebar: tags + related */}
              <aside className="space-y-6">
                {post.tags && post.tags.length > 0 && (
                  <div className="bg-card border border-border rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-text-primary mb-3">
                      Topics in this article
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {post.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2.5 py-1 rounded-full bg-muted text-xs text-text-secondary"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {relatedPosts.length > 0 && (
                  <RelatedPosts posts={relatedPosts} />
                )}

                <ResourcesSection />
              </aside>
            </div>
          </article>

          <section className="mt-12">
            <NewsletterSection />
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default BlogPostPage;
